import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { progress } from "../store/progress";
import { settings } from "../store/settings";
import { COLS_OPTIONS } from "../game/session";
import { PUZZLES } from "../game/puzzles";
import { todayKey } from "../game/daily";
import { openProfilePicker } from "./profile";
import { ICONS } from "./icons";

export class HomeScreen implements Screen {
  private off?: () => void;
  constructor(private nav: Nav) {}

  private bgEl?: HTMLElement;

  mount(host: HTMLElement): void {
    // Full-screen home background image (with a readability overlay).
    this.bgEl = el("div", "home-bg");
    document.body.appendChild(this.bgEl);
    document.body.classList.add("home-active");

    const screen = el("div", "screen home");
    const top = buildTopbar({
      showStardust: true,
      character: true,
      onCharacterClick: () => openProfilePicker(host, () => top.refresh()),
      onSettings: () => this.nav.settings(),
    });

    const hero = el("div", "hero");
    hero.innerHTML = `
      <h1 data-i18n="app.title"></h1>
      <p data-i18n="app.subtitle"></p>`;

    const menu = el("div", "home-menu");
    const campaignBtn = el("button", "menu-btn m-campaign");
    campaignBtn.innerHTML = `<span data-i18n="menu.campaign"></span><small id="campaign-prog"></small>`;
    campaignBtn.onclick = () => this.nav.campaign();

    const dailyBtn = el("button", "menu-btn m-daily");
    dailyBtn.innerHTML = `<span data-i18n="menu.daily"></span><small id="daily-state"></small>`;
    dailyBtn.onclick = () => this.nav.daily();

    const endlessBtn = el("button", "menu-btn m-endless");
    endlessBtn.innerHTML = `<span data-i18n="menu.endless"></span><small id="endless-best"></small>`;
    endlessBtn.onclick = () => this.nav.endless();

    const puzzleBtn = el("button", "menu-btn m-puzzle");
    puzzleBtn.innerHTML = `<span data-i18n="menu.puzzles"></span><small id="puzzle-prog"></small>`;
    puzzleBtn.onclick = () => this.nav.puzzles();

    // Global board-width picker — the ONLY place columns are chosen. The width
    // applies to campaign, daily, and endless. Wider boards unlock by clearing
    // World 1 at the previous width (4 → 5 → 6).
    const maxCols = progress.maxColsUnlocked();
    if (settings.get("boardCols") > maxCols) settings.set("boardCols", maxCols);

    const colPick = el("div", "col-pick");
    const colLabel = el("span", "col-pick-label");
    colLabel.dataset.i18n = "set.columns";
    colPick.appendChild(colLabel);
    const colGroup = el("div", "segmented");
    for (const c of COLS_OPTIONS) {
      const locked = c > maxCols;
      const b = el(
        "button",
        `seg ${c === settings.get("boardCols") ? "active" : ""} ${locked ? "locked" : ""}`,
      );
      b.innerHTML = locked ? `${c} 🔒` : String(c);
      if (locked) {
        b.disabled = true;
        b.title = t(c >= 6 ? "cols.unlock.6" : "cols.unlock.5");
      } else {
        b.onclick = () => {
          settings.set("boardCols", c);
          colGroup.querySelectorAll(".seg").forEach((e) => e.classList.remove("active"));
          b.classList.add("active");
        };
      }
      colGroup.appendChild(b);
    }
    colPick.appendChild(colGroup);

    menu.append(campaignBtn, dailyBtn, endlessBtn, puzzleBtn, colPick);

    // Secondary icon row.
    const iconRow = el("div", "home-icons");
    const iconBtn = (key: string, icon: string, on: () => void, badgeId?: string): HTMLElement => {
      const b = el("button", "icon-tile");
      b.innerHTML = `<span class="it-icon">${icon}</span><span class="it-label" data-i18n="${key}"></span>${
        badgeId ? `<small id="${badgeId}"></small>` : ""
      }`;
      b.onclick = on;
      return b;
    };
    iconRow.append(
      iconBtn("menu.skills", ICONS.skills, () => this.nav.skills()),
      iconBtn("menu.achievements", ICONS.achievements, () => this.nav.achievements(), "ach-badge"),
      iconBtn("menu.leaderboard", ICONS.leaderboard, () => this.nav.leaderboard()),
      iconBtn("menu.collection", "📖", () => this.nav.collection()),
      iconBtn("menu.cosmetics", "🎨", () => this.nav.cosmetics()),
    );

    screen.append(top.node, hero, menu, iconRow);
    host.appendChild(screen);

    const refresh = (): void => {
      top.refresh();
      screen.querySelectorAll<HTMLElement>("[data-i18n]").forEach((n) => {
        n.textContent = t(n.dataset.i18n!);
      });
      const p = progress.snapshot;
      const cols = progress.effectiveCols(settings.get("boardCols"));
      const stars = progress.totalStars(cols);
      const cp = screen.querySelector("#campaign-prog");
      if (cp) cp.textContent = `★ ${stars}`;
      const eb = screen.querySelector("#endless-best");
      if (eb) eb.textContent = p.bestEndless ? `${t("hud.best")} ${p.bestEndless}` : "";
      const ds = screen.querySelector("#daily-state");
      if (ds) {
        const claimed = progress.dailyClaimed(todayKey());
        ds.textContent = claimed
          ? `✓ 🔥${p.dailyStreak}`
          : t("daily.ready");
      }
      const pp = screen.querySelector("#puzzle-prog");
      if (pp) {
        const solved = PUZZLES.filter((z) => progress.starsFor(z.id, z.gridCols) > 0).length;
        pp.textContent = `${solved}/${PUZZLES.length}`;
      }
      // Badge the achievements tile with the number of claimable daily rewards.
      const day = todayKey();
      const claimable =
        (progress.loginRewardState(day).claimed ? 0 : 1) +
        progress.dailyMissions(day, cols).filter((m) => m.done && !m.claimed).length;
      const ab = screen.querySelector("#ach-badge");
      if (ab) ab.textContent = claimable > 0 ? String(claimable) : "";
    };
    refresh();
    this.off = i18n.onChange(refresh);
  }

  unmount(): void {
    this.off?.();
    this.bgEl?.remove();
    document.body.classList.remove("home-active");
  }
}
